import { FormEvent, useCallback, useEffect, useState } from "react";
import { Save, RotateCcw, SlidersHorizontal } from "lucide-react";
import { apiRequest, formatDate } from "../../core/common/api";
import {
  AmountField,
  ClubValue,
  IncomeForm,
  RankForm,
  TeamKey,
  incomePayload,
  milestoneTotals,
  teamTargets,
  toIncomeForm,
  validateIncomeForm,
} from "./clubIncomeForm";
import "./ClubIncome.css";

const rupees = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);

type NumberFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  max?: number;
  min?: number;
  whole?: boolean;
};
const NumberField = ({
  label,
  value,
  onChange,
  max = 1e12,
  min = 0,
  whole = false,
}: NumberFieldProps) => (
  <label className="form-field">
    <span className="modal-label">{label}</span>
    <input
      className="admin-input"
      type="number"
      inputMode={whole ? "numeric" : "decimal"}
      required
      min={min}
      max={max}
      step={whole ? "1" : "0.01"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const ClubIncome = () => {
  const [form, setForm] = useState<IncomeForm | null>(null);
  const [saved, setSaved] = useState<IncomeForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [conflict, setConflict] = useState(false);
  const [selected, setSelected] = useState<ClubValue>(1);

  const loadPlan = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const result = await apiRequest("/club-income", "GET");
      if (!result.success || !result.plan)
        throw new Error(
          result.message || "Unable to load club income settings.",
        );
      const next = toIncomeForm(result.plan);
      setForm(next);
      setSaved(next);
      setConflict(false);
    } catch (err: any) {
      setError(
        err.message || "Unable to load club income settings. Please retry.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const errors = form ? validateIncomeForm(form) : [];
  const club = form?.clubs.find((item) => item.club === selected);
  const currentRank = form?.ranks.find((item) => item.rank === selected);
  const poolPercent =
    form?.ranks.reduce((sum, rank) => sum + Number(rank.percent), 0) || 0;

  const edit = (update: (current: IncomeForm) => IncomeForm) => {
    setForm((current) => (current ? update(current) : current));
    setSuccess("");
  };
  const editClub = (patch: { name?: string; active?: boolean }) =>
    edit((current) => ({
      ...current,
      clubs: current.clubs.map((item) =>
        item.club === selected ? { ...item, ...patch } : item,
      ),
    }));
  const editMilestone = (
    sales: number,
    patch: Partial<Record<AmountField, string>>,
  ) =>
    edit((current) => ({
      ...current,
      clubs: current.clubs.map((item) =>
        item.club !== selected
          ? item
          : {
              ...item,
              milestones: item.milestones.map((m) =>
                m.sales === sales ? { ...m, ...patch } : m,
              ),
            },
      ),
    }));
  const editRank = (key: string, patch: Partial<RankForm>) =>
    edit((current) => ({
      ...current,
      ranks: current.ranks.map((rank) =>
        rank.rank === key ? { ...rank, ...patch } : rank,
      ),
    }));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form || saving || !dirty || errors.length || conflict) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const result = await apiRequest(
        "/club-income",
        "PATCH",
        incomePayload(form),
      );
      if (!result.success || !result.plan)
        throw new Error(result.message || "Unable to save settings.");
      const next = toIncomeForm(result.plan);
      setForm(next);
      setSaved(next);
      setSuccess(
        "Club income settings saved. New payouts will use these values.",
      );
    } catch (err: any) {
      const message =
        err.message ||
        "Unable to save. Your changes are still here; please retry.";
      setError(message);
      setConflict(/plan changed/i.test(message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="detail-page club-income-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Configuration</div>
          <h1 className="page-title">
            Club <em>Income</em>
          </h1>
          <div className="page-sub">
            Manage club payouts and company profit shares. Saved changes apply
            to new payouts.
          </div>
        </div>
      </div>

      {error && (
        <div className="ci-notice ci-error" role="alert">
          <p>{error}</p>
          {conflict && (
            <p>
              Another admin updated this plan. Load the latest settings, then
              reapply your changes.
            </p>
          )}
          <button
            type="button"
            className="btn-outline"
            disabled={loading || saving}
            onClick={() => void loadPlan()}
          >
            {conflict
              ? "Discard edits & load latest"
              : "Retry loading settings"}
          </button>
        </div>
      )}
      {success && (
        <div className="ci-notice ci-success" role="status">
          {success}
        </div>
      )}
      {loading && (
        <div className="loading-state" role="status">
          Loading club income settings…
        </div>
      )}

      {form && !loading && (
        <form onSubmit={save}>
          <fieldset className="ci-fieldset" disabled={saving}>
            <div className="ci-toolbar">
              <div>
                <SlidersHorizontal size={18} aria-hidden="true" />
                <span>Club 1–6 + independent Leader / Director / Partner / Royalty ranks</span>
              </div>
              <span className="text-muted-sm">
                Last saved: {formatDate(form.updatedAt)}
              </span>
            </div>
            <div
              className="ci-tabs"
              role="group"
              aria-label="Choose club or rank settings"
            >
              {form.clubs.map((item) => (
                <button
                  key={item.club}
                  type="button"
                  aria-pressed={selected === item.club}
                  className={`ci-tab ${selected === item.club ? "selected" : ""}`}
                  onClick={() => setSelected(item.club)}
                >
                  Club {item.club}
                  {!item.active && (
                    <span
                      className="ci-dot"
                      title="Payouts paused"
                      aria-label="Payouts paused"
                    />
                  )}
                </button>
              ))}
              {form.ranks.map((item) => (
                <button
                  key={item.rank}
                  type="button"
                  className={`ci-tab ${selected === item.rank ? "selected" : ""}`}
                  aria-pressed={selected === item.rank}
                  onClick={() => setSelected(item.rank)}
                >
                  {item.rank}
                </button>
              ))}
            </div>

            <p className="ci-note">
              Current selection:{" "}
              {typeof selected === "number" ? `Club ${selected}` : selected}.
              {club ? `Next: ${club.nextClub ? `Club ${club.nextClub}` : "Final club"}.` : "Rank qualification is independent of club progress."}
              Each month, the highest completed rank target applies. Club payouts continue alongside rank income.
            </p>
            {club && (
              <>
                <section className="table-card">
                  <div className="admin-card-body ci-club-heading">
                    <label className="form-field">
                      <span className="modal-label">Club {club.club} name</span>
                      <input
                        className="admin-input"
                        required
                        maxLength={80}
                        value={club.name}
                        onChange={(event) =>
                          editClub({ name: event.target.value })
                        }
                      />
                    </label>
                    <label className="ci-active">
                      <input
                        type="checkbox"
                        checked={club.active}
                        onChange={(event) =>
                          editClub({ active: event.target.checked })
                        }
                      />
                      <span>
                        <strong>
                          {club.active ? "Payouts active" : "Payouts paused"}
                        </strong>
                        <small>Applies when you save changes.</small>
                      </span>
                    </label>
                  </div>
                  {!club.active && (
                    <p className="ci-note">
                      This club’s payouts and progression are paused. After
                      resuming, eligible milestones process on the next network
                      update.
                    </p>
                  )}
                </section>
                <div className="ci-milestones">
                  {club.milestones.map((m) => {
                    const totals = milestoneTotals(m);
                    const balanced = totals.contribution === totals.allocated;
                    return (
                      <section
                        className="table-card"
                        key={`${club.club}-${m.sales}`}
                      >
                        <div className="table-head">
                          <h2>{m.sales === 3 ? "First stage · 3 sales" : "Second stage · 9 groups × 3"}</h2>
                          <p>
                            {m.sales === 3
                              ? "3 users directly below the member"
                              : "Each of the 9 grandchildren has 3 users: 27 third-level placements"}
                          </p>
                        </div>
                        <div className="admin-card-body ci-fields">
                          <NumberField
                            label={m.sales === 9 ? "Contribution per complete group (₹)" : "Contribution per sale (₹)"}
                            value={m.profitContribution}
                            onChange={(value) =>
                              editMilestone(m.sales, {
                                profitContribution: value,
                              })
                            }
                          />
                          <div className="ci-total">
                            <span>Total contribution</span>
                            <strong>{rupees(totals.contribution)}</strong>
                            <small>{m.sales} × contribution per {m.sales === 9 ? "complete group" : "sale"}</small>
                          </div>
                          <NumberField
                            label="Upgrade to second placement ancestor (₹)"
                            value={m.upgradeFund}
                            onChange={(value) =>
                              editMilestone(m.sales, { upgradeFund: value })
                            }
                          />
                          <NumberField
                            label="Working Fund (₹)"
                            value={m.workingFund}
                            onChange={(value) =>
                              editMilestone(m.sales, { workingFund: value })
                            }
                          />
                          <NumberField
                            label="Direct sponsor Up-line (₹)"
                            value={m.uplineFund}
                            onChange={(value) =>
                              editMilestone(m.sales, { uplineFund: value })
                            }
                          />
                          <div
                            className={`ci-total ${balanced ? "ci-balanced" : "ci-unbalanced"}`}
                            aria-live="polite"
                          >
                            <span>Allocated total</span>
                            <strong>{rupees(totals.allocated)}</strong>
                            <small>
                              {balanced
                                ? "Matches contribution"
                                : `Must equal ${rupees(totals.contribution)}`}
                            </small>
                          </div>
                          <div className="ci-repurchase">
                            <h3>Re-purchases Fund</h3>
                            <p>
                              Fixed credit in rupees, separate from the
                              contribution total.
                            </p>
                            <div className="ci-fields">
                              <NumberField
                                label="Repurchase (₹)"
                                value={m.repurchase}
                                onChange={(value) =>
                                  editMilestone(m.sales, { repurchase: value })
                                }
                              />
                            </div>
                            <p className="ci-repurchase-total">
                              Credit:{" "}
                              <strong>{rupees(totals.repurchase)}</strong> ·
                              fixed amount
                            </p>
                          </div>
                        </div>
                      </section>
                    );
                  })}
                </div>
                <p className="ci-note">
                  Upgrade is credited directly to the member two placement levels
                  above, or Company ID when that ancestor does not exist.
                  No intermediate member credit or debit is posted. Each income
                  goes to the entitled recipient’s buyerId account when present,
                  in the same fund, with the IDs recorded in history.
                  Working and Re-purchases go to the
                  member; Up-line goes to their direct sponsor. The Company ID
                  keeps the initial Club 1 contribution; later company stages
                  use received upgrades without another self-payout.
                </p>
              </>
            )}

            {currentRank && (
              <>
                <div className="ci-notice">
                  <strong>Combined profit shares: {poolPercent}% / 100%</strong>
                  <p>
                    Each pool is shared equally among qualifying members from any club.
                    Club income continues alongside rank income. Set a pool to 0% to stop its
                    payments.
                  </p>
                </div>
                <div className="ci-ranks">
                  {[currentRank].map((rank) => (
                    <section className="table-card" key={rank.rank}>
                      <div className="table-head">
                        <h2>{rank.rank} Rank</h2>
                      </div>
                      <div className="admin-card-body ci-fields">
                        <label className="form-field">
                          <span className="modal-label">Display name</span>
                          <input
                            className="admin-input"
                            required
                            maxLength={80}
                            value={rank.name}
                            onChange={(event) =>
                              editRank(rank.rank, { name: event.target.value })
                            }
                          />
                        </label>
                        <NumberField
                          label="Company profit share (%)"
                          max={100}
                          value={rank.percent}
                          onChange={(value) =>
                            editRank(rank.rank, { percent: value })
                          }
                        />
                        <NumberField
                          label="Sales required every month"
                          whole
                          max={1e6}
                          value={rank.monthSales}
                          onChange={(value) =>
                            editRank(rank.rank, { monthSales: value })
                          }
                        />
                        {teamTargets[rank.rank].map((key: TeamKey) => (
                          <NumberField
                            key={key}
                            label={`${key.charAt(0).toUpperCase() + key.slice(1)}s required in team`}
                            whole
                            max={1e6}
                            value={rank.need[key]}
                            onChange={(value) =>
                              editRank(rank.rank, {
                                need: { ...rank.need, [key]: value },
                              })
                            }
                          />
                        ))}
                        {rank.rank === "Royalty" && (
                          <>
                            <NumberField
                              label="Rank target window (months)"
                              whole
                              min={1}
                              max={120}
                              value={rank.targetMonths}
                              onChange={(value) =>
                                editRank(rank.rank, { targetMonths: value })
                              }
                            />
                            <p className="ci-wide text-muted-sm">
                              Achieve the initial Royalty target within this
                              window after achieving Partner. Monthly sales are
                              required every month.
                            </p>
                          </>
                        )}
                      </div>
                    </section>
                  ))}
                </div>
              </>
            )}

            {errors.length > 0 && (
              <div className="ci-notice ci-error" role="alert">
                <strong>Fix these values before saving:</strong>
                <ul>
                  {errors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="ci-savebar">
              <div>
                <strong>
                  {dirty ? "Unsaved changes" : "All changes saved"}
                </strong>
                <small>Save applies changes across all clubs and ranks.</small>
              </div>
              <div className="ci-actions">
                <button
                  type="button"
                  className="btn-sm btn-outline"
                  disabled={!dirty || saving}
                  onClick={() => {
                    setForm(saved);
                    setSuccess("");
                    if (!conflict) setError("");
                  }}
                >
                  <RotateCcw size={16} />
                  Reset edits
                </button>
                <button
                  type="submit"
                  className="btn-sm btn-primary"
                  disabled={!dirty || saving || errors.length > 0 || conflict}
                >
                  <Save size={16} />
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </fieldset>
        </form>
      )}
    </div>
  );
};

export default ClubIncome;
